import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { EmployeePicker } from "@/components/employee-picker";
import { JobPicker } from "@/components/job-picker";
import { useAuth } from "@/app/auth-context";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "@/hooks/use-toast";
import {
  tasksApi,
  TASK_TYPES,
  TASK_TYPE_LABELS,
  TASK_PRIORITY_LABELS,
  taskCreateClaim,
  type TaskType,
  type TaskDetailDto,
  type CreateTaskRequest,
} from "@/lib/tasks";

export interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presetType?: TaskType;
  presetJobId?: number;
  presetJobLabel?: string;
  presetQuoteId?: number;
  onCreated?: (task: TaskDetailDto) => void;
}

const PRIORITIES = [1, 2, 3, 4];
const FALLBACK_TYPE: TaskType = "Admin";

/**
 * Three-section Create Task dialog (Type → Job link → Details). Matches the
 * SendEmailDialog contract: controlled `open`, draft state resets every time
 * `open` flips to true (re-seeded from presets + `me`), submit closes only on
 * success and surfaces failures inline. Type-specific fields render by the
 * selected task type.
 */
export function CreateTaskDialog({
  open,
  onOpenChange,
  presetType,
  presetJobId,
  presetJobLabel,
  presetQuoteId,
  onCreated,
}: CreateTaskDialogProps) {
  const { me } = useAuth();
  const { has } = usePermissions();
  const queryClient = useQueryClient();

  const creatableTypes = useMemo(
    () => TASK_TYPES.filter((t) => has(taskCreateClaim(t))),
    // `has` is stable per render of usePermissions; recompute when presets change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  const defaultType = presetType ?? creatableTypes[0] ?? FALLBACK_TYPE;

  // Section 1: type
  const [taskType, setTaskType] = useState<TaskType>(defaultType);
  // Section 2: job link
  const [jobLinked, setJobLinked] = useState<boolean>(presetJobId != null);
  const [jobId, setJobId] = useState<number | null>(presetJobId ?? null);
  const [jobLabel, setJobLabel] = useState<string | null>(presetJobLabel ?? null);
  // Section 3: details
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedEmployeeId, setAssignedEmployeeId] = useState<number | null>(
    me?.employeeId ?? null,
  );
  const [assigneeLabel, setAssigneeLabel] = useState<string | null>(
    me?.displayName ?? null,
  );
  const [dueDateUtc, setDueDateUtc] = useState<string | null>(null);
  const [priority, setPriority] = useState<number>(2);
  // Type-specific
  const [followUpKind, setFollowUpKind] = useState("");
  const [badgeQuantity, setBadgeQuantity] = useState("");
  const [badgeStage, setBadgeStage] = useState("");
  const [productionStage, setProductionStage] = useState("");
  const [expectedMinutes, setExpectedMinutes] = useState("");
  const [artworkAction, setArtworkAction] = useState("");

  const [error, setError] = useState<string | null>(null);

  // Reset draft state whenever the dialog opens (re-seed from presets + me).
  useEffect(() => {
    if (open) {
      setTaskType(presetType ?? creatableTypes[0] ?? FALLBACK_TYPE);
      setJobLinked(presetJobId != null);
      setJobId(presetJobId ?? null);
      setJobLabel(presetJobLabel ?? null);
      setTitle("");
      setDescription("");
      setAssignedEmployeeId(me?.employeeId ?? null);
      setAssigneeLabel(me?.displayName ?? null);
      setDueDateUtc(null);
      setPriority(2);
      setFollowUpKind("");
      setBadgeQuantity("");
      setBadgeStage("");
      setProductionStage("");
      setExpectedMinutes("");
      setArtworkAction("");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (body: CreateTaskRequest) => tasksApi.create(body),
    onSuccess: (task) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Task created");
      onCreated?.(task);
      onOpenChange(false);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Failed to create task";
      setError(message);
      toast.error(message);
    },
  });

  function buildBody(): CreateTaskRequest {
    const body: CreateTaskRequest = {
      taskType,
      title: title.trim(),
      description: description.trim() || null,
      priority,
      assignedEmployeeId,
      dueDateUtc,
    };
    if (jobLinked && jobId != null) body.jobId = jobId;
    if (presetQuoteId != null && taskType === "Admin") body.quoteId = presetQuoteId;

    // Only the fields for the chosen type.
    switch (taskType) {
      case "Admin":
        body.followUpKind = followUpKind.trim() || null;
        break;
      case "Badge":
        body.badgeQuantity = badgeQuantity.trim() ? Number(badgeQuantity) : null;
        body.badgeStage = badgeStage.trim() || null;
        break;
      case "Production":
        body.productionStage = productionStage.trim() || null;
        body.expectedMinutes = expectedMinutes.trim() ? Number(expectedMinutes) : null;
        break;
      case "Artwork":
        body.artworkAction = artworkAction.trim() || null;
        break;
    }
    return body;
  }

  function doSubmit() {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setError(null);
    createMutation.mutate(buildBody());
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    doSubmit();
  };

  const typeLocked = presetType != null;
  const isPending = createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="task-description">Description</Label>
            <Textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="task-type">Type</Label>
            <Select
              value={taskType}
              onValueChange={(v) => setTaskType(v as TaskType)}
              disabled={typeLocked}
            >
              <SelectTrigger id="task-type" aria-label="Task type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(typeLocked ? [defaultType] : creatableTypes).map((t) => (
                  <SelectItem key={t} value={t}>
                    {TASK_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {taskType === "Admin" && (
            <div className="space-y-1">
              <Label htmlFor="task-followup">Follow-up kind</Label>
              <Input
                id="task-followup"
                value={followUpKind}
                onChange={(e) => setFollowUpKind(e.target.value)}
              />
            </div>
          )}
          {taskType === "Badge" && (
            <>
              <div className="space-y-1">
                <Label htmlFor="task-badge-qty">Badge quantity</Label>
                <Input
                  id="task-badge-qty"
                  type="number"
                  value={badgeQuantity}
                  onChange={(e) => setBadgeQuantity(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-badge-stage">Badge stage</Label>
                <Input
                  id="task-badge-stage"
                  value={badgeStage}
                  onChange={(e) => setBadgeStage(e.target.value)}
                />
              </div>
            </>
          )}
          {taskType === "Production" && (
            <>
              <div className="space-y-1">
                <Label htmlFor="task-prod-stage">Production stage</Label>
                <Input
                  id="task-prod-stage"
                  value={productionStage}
                  onChange={(e) => setProductionStage(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task-prod-minutes">Expected minutes</Label>
                <Input
                  id="task-prod-minutes"
                  type="number"
                  value={expectedMinutes}
                  onChange={(e) => setExpectedMinutes(e.target.value)}
                />
              </div>
            </>
          )}
          {taskType === "Artwork" && (
            <div className="space-y-1">
              <Label htmlFor="task-artwork-action">Artwork action</Label>
              <Input
                id="task-artwork-action"
                value={artworkAction}
                onChange={(e) => setArtworkAction(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label>Assignee</Label>
            <EmployeePicker
              value={assignedEmployeeId}
              valueLabel={assigneeLabel}
              onChange={(id, emp) => {
                setAssignedEmployeeId(id);
                setAssigneeLabel(emp?.fullName ?? null);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="task-due-date">Due date</Label>
            <Input
              id="task-due-date"
              type="date"
              value={dueDateUtc ? dueDateUtc.slice(0, 10) : ""}
              onChange={(e) => {
                const val = e.target.value;
                setDueDateUtc(val ? `${val}T00:00:00.000Z` : null);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="task-priority">Priority</Label>
            <Select
              value={String(priority)}
              onValueChange={(v) => setPriority(Number(v))}
            >
              <SelectTrigger id="task-priority" aria-label="Priority" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={String(p)}>
                    {TASK_PRIORITY_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="task-job-toggle">Linked to a job</Label>
            <Switch
              id="task-job-toggle"
              checked={jobLinked}
              onCheckedChange={(v) => {
                setJobLinked(v);
                if (!v) {
                  setJobId(null);
                  setJobLabel(null);
                }
              }}
            />
          </div>
          {jobLinked && (
            <JobPicker
              value={jobId}
              valueLabel={jobLabel}
              onChange={(id, job) => {
                setJobId(id);
                setJobLabel(job?.jobReferenceNo ?? null);
              }}
            />
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Creating…" : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
